/**
 * Nubank Error Config Viewer
 *
 * Dev-only page that shows all error configurations using the actual
 * nubank GenericResponseScreen component for accurate previews.
 *
 * This implementation uses the shared useErrorConfigViewer hook and
 * ErrorConfigViewerLayout from @hopper-b2b/checkout, with nubank-specific
 * rendering for the full-screen preview using GenericResponseScreen.
 */
import {
  ErrorConfigViewerLayout,
  useErrorConfigViewer,
  type ErrorPreviewProps,
} from "@hopper-b2b/checkout";
import { Dialog } from "@material-ui/core";
import clsx from "clsx";

import { GenericResponseScreen } from "../GenericResponseScreen";
import "./styles.scss";

/**
 * Custom card preview for nubank - shows text preview with styling
 */
const NubankCardPreview = ({
  title,
  subtitle,
  primaryButtonText,
  secondaryButtonText,
  titleMissing,
  subtitleMissing,
}: ErrorPreviewProps) => (
  <>
    <div
      className={clsx("nubank-error-config-viewer__title", {
        "nubank-error-config-viewer__missing": titleMissing,
      })}
    >
      {title}
    </div>
    <div
      className={clsx("nubank-error-config-viewer__subtitle", {
        "nubank-error-config-viewer__missing": subtitleMissing,
      })}
    >
      {subtitle}
    </div>
    {/* Buttons render in same order as MobileFloatingSection: secondary first, primary second */}
    <div className="nubank-error-config-viewer__buttons">
      {secondaryButtonText && (
        <span className="nubank-error-config-viewer__secondary-btn">
          {secondaryButtonText}
        </span>
      )}
      <span className="nubank-error-config-viewer__primary-btn">
        {primaryButtonText || "(empty)"}
      </span>
    </div>
    <div className="nubank-error-config-viewer__click-hint">
      Click to preview full screen
    </div>
  </>
);

/**
 * Full-screen preview props with navigation
 */
interface FullScreenPreviewProps extends ErrorPreviewProps {
  goToNext: () => void;
  goToPrev: () => void;
  currentIndex: number;
  totalCount: number;
}

/**
 * Full-screen preview using the actual nubank GenericResponseScreen
 */
const NubankFullScreenPreview = ({
  entry,
  title,
  subtitle,
  primaryButtonText,
  secondaryButtonText,
  onClose,
  goToNext,
  goToPrev,
  currentIndex,
  totalCount,
}: FullScreenPreviewProps) => (
  <Dialog open fullScreen className="nubank-error-config-viewer__dialog">
    {/* Navigation overlay */}
    <div className="nubank-error-config-viewer__nav-overlay">
      <div className="nubank-error-config-viewer__nav-info">
        <span className="nubank-error-config-viewer__nav-error-code">
          {entry.errorCode}
        </span>
        <span className="nubank-error-config-viewer__nav-counter">
          {currentIndex + 1} / {totalCount}
        </span>
      </div>
      <div className="nubank-error-config-viewer__nav-buttons">
        <button
          type="button"
          className="nubank-error-config-viewer__nav-btn"
          onClick={goToPrev}
          title="Previous (← or ↑)"
        >
          ← Prev
        </button>
        <button
          type="button"
          className="nubank-error-config-viewer__nav-btn"
          onClick={goToNext}
          title="Next (→ or ↓)"
        >
          Next →
        </button>
        <button
          type="button"
          className="nubank-error-config-viewer__nav-btn nubank-error-config-viewer__nav-btn--close"
          onClick={onClose}
          title="Close (Esc)"
        >
          ✕ Close
        </button>
      </div>
    </div>

    <GenericResponseScreen
      title={title}
      subtitle={subtitle}
      primaryButtonText={primaryButtonText || "(empty)"}
      primaryOnClick={onClose}
      secondaryButtonText={secondaryButtonText}
      secondaryOnClick={secondaryButtonText ? onClose : undefined}
      className={entry.className ?? "error"}
      onBack={onClose}
    />
  </Dialog>
);

/**
 * Nubank-specific Error Config Viewer
 *
 * Uses the shared hook and layout from @hopper-b2b/checkout,
 * with custom rendering via GenericResponseScreen.
 */
export function NubankErrorConfigViewer() {
  const viewer = useErrorConfigViewer();

  return (
    <ErrorConfigViewerLayout
      viewer={viewer}
      title="🔍 Nubank Error Configuration Viewer"
      subtitle="Visual preview using the actual nubank GenericResponseScreen component."
      className="nubank-error-config-viewer"
      renderCardPreview={(props) => <NubankCardPreview {...props} />}
      renderFullScreenPreview={(props) => (
        <NubankFullScreenPreview {...props} />
      )}
    />
  );
}
